import { getAuth } from 'firebase/auth';
import { updateSessionObj, getSessionObj, getObj } from './localStorage';
import { getUrlParameter, updateUrlParameter, getUrlParameters } from './window';

const NEW_CUSTOMER_WINDOW_MS = 30 * 60 * 1000;

/**
 * Returns true when the signed-in Firebase user was created within the recent
 * NEW_CUSTOMER_WINDOW_MS window — used as the `new_customer` signal for
 * Google Ads PMax New Customer Acquisition bidding.
 */
export const isNewCustomer = () => {
  try {
    const creationTime = getAuth().currentUser?.metadata?.creationTime;
    if (!creationTime) return false;
    return Date.now() - new Date(creationTime).getTime() < NEW_CUSTOMER_WINDOW_MS;
  } catch {
    return false;
  }
};

/**
 * Get user data from localStorage (auth state is stored there by user.js)
 * This avoids circular dependencies by only using localStorage
 */
const getUserData = () => {
  const user = getObj('user') || {};
  return {
    userId: user.userId,
    email: user.profile?.email || user.auth?.email,
    profile: user.profile || {},
  };
};

/**
 * Helper to get cookie value by name
 * Used to retrieve Meta _fbc and _fbp cookies for advanced matching
 */
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// Product SKU constants (avoiding circular dependency with products.js)
const DEFAULT_MEMBERSHIP_SKU = '2';

// Session key for tracking add_to_cart firing
const ADD_TO_CART_SESSION_KEY = 'addToCartFired';
// Session key for tracking begin_checkout firing
const BEGIN_CHECKOUT_SESSION_KEY = 'beginCheckoutFired';

/**
 * Check if add_to_cart has already been fired this session
 * Uses sessionStorage to persist across page redirects within the same session
 */
export const hasAddToCartFired = () => {
  try {
    return sessionStorage.getItem(ADD_TO_CART_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Mark add_to_cart as fired for this session
 */
export const markAddToCartFired = () => {
  try {
    sessionStorage.setItem(ADD_TO_CART_SESSION_KEY, 'true');
  } catch {
    // Ignore storage errors
  }
};

/**
 * Clear add_to_cart fired flag (e.g., when user logs out or starts new funnel)
 */
export const clearAddToCartFired = () => {
  try {
    sessionStorage.removeItem(ADD_TO_CART_SESSION_KEY);
  } catch {
    // Ignore storage errors
  }
};

/**
 * Check if begin_checkout has already been fired this session
 * Uses sessionStorage to persist across page redirects within the same session
 */
export const hasBeginCheckoutFired = () => {
  try {
    return sessionStorage.getItem(BEGIN_CHECKOUT_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Mark begin_checkout as fired for this session
 */
export const markBeginCheckoutFired = () => {
  try {
    sessionStorage.setItem(BEGIN_CHECKOUT_SESSION_KEY, 'true');
  } catch {
    // Ignore storage errors
  }
};

// Funnel-cart SKUs that carry no collection/slug context locally — the SKU
// IS the variant. Generated by site/scripts/build-sku-variant-map.js from
// the same product source files build-product-feeds.js reads, so Pixel
// content_ids stay in lock-step with the Meta/Google/Pinterest feed rows.
// Add a new bare-SKU product → edit the source JSON → re-run build:sku-map.
import SKU_TO_VARIANT from '../site/data/sku-variant-map.json';

/**
 * Per-variant catalogue join key consumed by Meta/Google/Pinterest feeds +
 * Pixel events. Mirrors Shopify/Printful/Customily `variant_id` naming.
 * Returns `{collection}-{slug}-{size}` when both are known, falls back to
 * `{collection}-{slug}` for non-sized collections, then to the canonical
 * SKU map for known funnel SKUs, then to `String(sku)` as a last resort.
 *
 * @param {Object} product
 *   { sku, collection?, slug?, size?, variantId? }. If `variantId` is set
 *   (caller already resolved the composite), it's returned untouched.
 * @returns {string}
 */
export const variantId = (product = {}) => {
  if (product.variantId) return String(product.variantId);
  const { collection, slug, size, sku } = product;
  if (collection && slug) {
    return size ? `${collection}-${slug}-${size}` : `${collection}-${slug}`;
  }
  if (sku == null) return '';
  const mapped = SKU_TO_VARIANT[Number(sku)] || SKU_TO_VARIANT[String(sku)];
  if (mapped) return mapped;
  return String(sku);
};

/**
 * Generate a semi-unique event_id for GA4 / Meta deduplicatie.
 * @param {string} eventName
 * @returns {string}
 */
export const generateEventId = (eventName = 'evt') => {
  const ts = Date.now();
  const rand = Math.floor(Math.random() * 1e5)
    .toString()
    .padStart(5, '0');
  return `${eventName}_${ts}_${rand}`;
};

/**
 * Helper function to get fallback product names by type
 * @param {string} sku - Product SKU
 * @param {string} type - Product type
 * @returns {string} Product name
 */
const getFauxOrderProductName = (sku, type) => {
  // Fallback names based on type
  const fallbackNames = {
    subscription: 'Membership',
    gift: 'Gift Box',
    photobook: 'Photo Book',
    walldecor: 'Wall Decoration',
  };

  return fallbackNames[type] || 'Product';
};

/**
 * Create a standardized faux order object for tracking purposes
 * @param {Object} options - Configuration options for the faux order
 * @param {string} options.sku - Product SKU (defaults to URL param or basic membership)
 * @param {number} options.units - Product units (defaults to URL param or 1)
 * @param {string} options.type - Product type ('subscription', 'gift', etc.)
 * @param {string} options.name - Product name (auto-generated if not provided)
 * @param {number} options.unitPrice - Unit price in cents (defaults to URL param or 0)
 * @param {string} options.currency - Currency code (defaults to 'EUR')
 * @param {string} options.countryCode - Country code (required, defaults to 'NL')
 * @param {string} options.discountCode - Discount code (defaults to URL param)
 * @param {number} options.totalDiscount - Total discount in cents (defaults to 0)
 * @param {string} options.orderId - Order ID (optional, for fallback scenarios)
 * @param {string} options.userId - User ID (optional, for fallback scenarios)
 * @param {string} [options.variantId] - Pre-resolved catalogue variant id (KL-14191).
 *   When provided, downstream Pixel events emit this as item_id instead of the
 *   bare SKU — required for catalogue match-rate on photobook/walldecor PDPs.
 * @param {string} [options.collection] - Catalogue collection (e.g. `photobooks`)
 * @param {string} [options.slug] - Per-product slug (e.g. `africa`)
 * @param {string} [options.size] - Variant size (e.g. `large`)
 * @returns {Object} Standardized faux order object
 */
export const createFauxOrder = (options = {}) => {
  const {
    sku = getUrlParameter('sku') || DEFAULT_MEMBERSHIP_SKU,
    units = Number(getUrlParameter('units')) || 1,
    type = 'subscription',
    name = getFauxOrderProductName(sku, type),
    unitPrice = parseInt(getUrlParameter('price') || '0', 10),
    currency = 'EUR',
    countryCode = 'NL',
    discountCode = getUrlParameter('couponCode') || '',
    totalDiscount = 0,
    orderId,
    userId,
    variantId: variantIdOpt = null,
    collection = null,
    slug = null,
    size = null,
  } = options;

  const totalToCharge = unitPrice * units;

  const productLine = {
    sku: String(sku),
    name,
    type,
    unitPrice,
    units,
  };
  if (variantIdOpt) productLine.variantId = variantIdOpt;
  if (collection) productLine.collection = collection;
  if (slug) productLine.slug = slug;
  if (size) productLine.size = size;

  const fauxOrder = {
    countryCode,
    currency,
    totalToCharge,
    totalDiscount,
    discountCode,
    products: [productLine],
  };

  // Add optional fields if provided (for fallback scenarios)
  if (orderId) {
    fauxOrder.id = orderId;
  }
  if (userId || getUrlParameters().userId || getObj('user')?.userId) {
    fauxOrder.userId = userId || getUrlParameters().userId || getObj('user')?.userId;
  }

  return fauxOrder;
};

/**
 * Centralised wrapper for all dataLayer pushes.
 * Automatically attaches a unique event_id when not provided.
 * For ecommerce events with a transaction_id (order.id), use that for deduplication with sGTM.
 * This ensures browser and server events have matching IDs for Meta CAPI and TikTok.
 * @param {Object} payload full object to push; MUST include an `event` key.
 */
export const pushEvent = (payload) => {
  if (!payload?.event) {
    console.error('pushEvent: `event` property is required', payload);
    return;
  }

  // Use transaction_id (order.id) as event_id for ALL ecommerce events for sGTM deduplication.
  // This ensures browser and server events have matching IDs for Meta CAPI and TikTok.
  // The backend (klikkie-orders) sends the same event_id = order.id for all events.
  const transactionId = payload.ecommerce?.transaction_id;

  const enriched = {
    event_id: transactionId ? String(transactionId) : generateEventId(payload.event),
    ...payload,
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(enriched);
  console.log(`Pushed event "${enriched.event}"`, JSON.stringify(enriched));
};

export const trackCheckout = (event, order, additionalData = {}) => {
  const ecommerceEvents = [
    'begin_checkout',
    'add_shipping_info',
    'add_payment_info',
    'purchase',
    'view_item',
    'add_to_cart',
    'remove_from_cart',
  ];
  if (!ecommerceEvents.includes(event)) {
    throw new Error(`Event "${event}" is not allowed.`);
  }

  // Clear the previous objects
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });

  // Map event names to Meta types
  const metaTypeMap = {
    add_to_cart: 'add',
    view_content: 'content',
    view_item: 'detail',
    begin_checkout: 'checkout',
    add_payment_info: 'payment',
    add_shipping_info: 'shipping',
    purchase: 'purchase',
  };

  // Populate the items. item_id resolves to the composite variantId
  // ({collection}-{slug}-{size}) so Meta Pixel content_ids match the
  // catalogue feed; falls back to the raw SKU when the cart-line lacks
  // composite-ready fields.
  //
  // KL-14380 follow-up: drop items whose item_id stays bare-numeric (no
  // hyphen → not in any composite-id feed row). Accessories (extra pages
  // 613/614/615), the no-plan-picked sentinel sku=0, and assorted
  // bundle/legacy SKUs never appear in the catalogue and used to hit Meta
  // diagnostics as "Item deleted from catalog". Filtering at the Pixel
  // boundary keeps revenue attribution untouched (value is sourced from
  // order.totalToCharge independently) while stopping the false-flag noise.
  const items = (order.products || [])
    .map((product) => ({
      item_id: variantId(product),
      item_name: product.name,
      item_brand: 'klikkie',
      item_category: product.collection || product.type,
      price: product.unitPrice / 100,
      quantity: product.units,
    }))
    .filter((item) => typeof item.item_id === 'string' && item.item_id.includes('-'));

  // Defensive: an event with empty items[] becomes Meta "Missing content ID"
  // noise (the ATC fires on /membership/start with id= but the order has no
  // catalogue-mapped products, e.g. sku=0 / accessory-only carts). Skip the
  // dataLayer push entirely — the ecommerce signal isn't useful here anyway.
  if (items.length === 0) {
    console.warn(`Skipping ${event} push: no catalogue-mapped items after filter`, {
      orderId: order.id,
      rawProducts: order.products,
    });
    return;
  }

  // Populate ecommerce event
  const params = {
    event,
    country: order.countryCode,
    ecommerce: {
      type: metaTypeMap[event] || '',
      transaction_id: order.id,
      currency: order.currency,
      value: order.totalToCharge / 100,
      items,
      coupon: order.discountCode,
      discount: order.totalDiscount / 100,
    },
  };

  if (event === 'purchase') {
    params.new_customer = isNewCustomer();
  }


  // Build comprehensive user_data for enhanced conversions / advanced matching
  // Supports: Google Ads Enhanced Conversions, Meta CAPI, TikTok, etc.
  const userData = {};

  // Auto-fetch user data from Firebase auth and localStorage
  const authData = getUserData();
  const storedUser = getObj('user') || {};
  const storedProfile = storedUser.profile || {};

  // Email - primary identifier for all platforms
  // Fallback chain: explicit > auth > profile
  const email = additionalData.email || authData.email || storedProfile.email;
  if (email) {
    const normalizedEmail = email.toLowerCase().trim();
    userData.email = normalizedEmail;
    userData.email_address = normalizedEmail; // Facebook expects this field name
    userData.sha256_email_address = additionalData.email_sha256 || null;
  }

  // Phone number (if available)
  const phone = additionalData.phone || storedProfile.phonenumber;
  if (phone) {
    userData.phone_number = phone.replace(/\D/g, ''); // Digits only
  }

  // Address data (for Google Enhanced Conversions)
  // Fallback chain: explicit > cached address from localStorage
  // Ensure address is not an array (take first item if it is)
  let addressData = additionalData.address || getObj('address') || {};
  if (Array.isArray(addressData)) {
    console.warn('Address is an array, taking first item');
    addressData = addressData[0] || {};
  }

  const city = addressData.city || additionalData.city;
  const state = addressData.state || additionalData.state;
  const postalCode = addressData.zipcode || additionalData.postalCode;
  const street = addressData.address || additionalData.street;
  if (city) userData.city = city;
  if (state) userData.state = state;
  if (postalCode) userData.postal_code = postalCode;
  if (street) userData.street = street;

  // Name (for Google Enhanced Conversions)
  // Fallback chain: explicit > profile > address.fullname (split)
  let firstName = additionalData.firstName || storedProfile.name;
  let lastName = additionalData.lastName || storedProfile.surname;

  // If no name from profile, try to extract from address.fullname
  if (!firstName && !lastName && addressData.fullname) {
    const nameParts = addressData.fullname.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      [firstName] = nameParts;
      lastName = nameParts.slice(1).join(' ');
    } else if (nameParts.length === 1) {
      [firstName] = nameParts;
    }
  }

  if (firstName) userData.first_name = firstName;
  if (lastName) userData.last_name = lastName;

  // Country fallback: explicit > address > profile > order
  const country =
    additionalData.country ||
    addressData.countryCode ||
    storedProfile.countryCode ||
    order.countryCode;
  if (country) userData.country = country;

  // User/Customer ID (for cross-device matching)
  // Fallback chain: explicit > auth > localStorage > order
  const userId = additionalData.userId || authData.userId || storedUser.userId || order.userId;
  if (userId) {
    userData.user_id = userId;
    userData.external_id = additionalData.externalId || userId;
  }

  // Click IDs — critical for attribution. Survive iDEAL/Adyen redirect via
  // sessionStorage (set by retrieveUtmParameters on landing); gclid also
  // falls back to the _gcl_aw cookie (written by GTM's Conversion Linker;
  // format "GCL.<timestamp>.<gclid>").
  const urlParams = new URLSearchParams(window.location.search);
  const sessionUtm = { ...getAttributionCookie(), ...(getSessionObj('utm') || {}) };
  const gclAwGclid = (getCookie('_gcl_aw') || '').split('.').slice(2).join('.') || null;

  // key → optional extra fallback (after URL → session → additionalData)
  const CLICK_IDS = {
    fbclid: null,
    gclid: gclAwGclid,
    wbraid: null,
    gbraid: null,
    ttclid: null,
    msclkid: null,
    epik: null,
    scid: null,
  };
  for (const [key, extra] of Object.entries(CLICK_IDS)) {
    const value = urlParams.get(key) || sessionUtm[key] || additionalData[key] || extra;
    if (value) userData[key] = value;
  }

  // Meta cookies (separate from click IDs — sourced from cookies, not URL).
  // _fbc is written by the Pixel ONLY when fbclid is in the page URL. On a
  // cross-subdomain checkout (app.klikkie.com) the URL has no fbclid, so the
  // Pixel never writes _fbc there even though the .klikkie.com _kk_utm cookie
  // still carries the fbclid. Unlike the other click IDs (read from sessionUtm
  // above), fbc has no such fallback — so reconstruct it from that fbclid,
  // fb.1.<unix-ms>.<fbclid> per Meta's spec, when no real _fbc cookie exists.
  // Guarded on fbclid presence: no Meta click → no fbc (never fabricate one).
  const fbclidForFbc = urlParams.get('fbclid') || sessionUtm.fbclid || additionalData.fbclid;
  const fbc =
    urlParams.get('fbc') ||
    getCookie('_fbc') ||
    additionalData.fbc ||
    (fbclidForFbc ? `fb.1.${Date.now()}.${fbclidForFbc}` : null);
  const fbp = getCookie('_fbp') || additionalData.fbp;
  if (fbc) userData.fbc = fbc;
  if (fbp) userData.fbp = fbp;

  // Client/session info
  userData.client_ip_address = additionalData.ip || null; // Usually set server-side
  userData.client_user_agent = navigator.userAgent;

  // Only add user_data if we have any data
  if (Object.keys(userData).length > 0) {
    params.user_data = userData;
  }

  pushEvent(params);
};

// Re-export default helper for backwards compatibility (if any file imports default)
export default {
  trackCheckout,
  pushEvent,
  generateEventId,
};

/**
 * Set a first-party cookie. Used at landing to synthesize Meta's `_fbc` from
 * `fbclid` so server-side CAPI can match conversions even when the Pixel
 * never loaded (Instagram in-app webview, cross-context returns).
 * Spec: fb.1.{unix-ms}.{fbclid}  — https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
 */
const setFirstPartyCookie = (name, value, days = 90) => {
  try {
    const maxAge = days * 24 * 60 * 60;
    const host = window.location.hostname;
    // Strip leading subdomain so cookie is readable across www/nl/de/fr/es subdomains.
    const parts = host.split('.');
    const domain = parts.length > 2 ? `.${parts.slice(-2).join('.')}` : host;
    document.cookie = `${name}=${value}; Max-Age=${maxAge}; Path=/; Domain=${domain}; SameSite=Lax; Secure`;
  } catch (_) {
    // Ignore — cookie write failures shouldn't block landing.
  }
};

// Cross-subdomain attribution store (KL-14380). sessionStorage is per-origin
// and per-tab; a fr.klikkie.com landing's utm/fbclid was lost the moment the
// user reached the www.klikkie.com checkout. The _fbp/_ga/_fbc cookies
// survived because they're scoped to the parent domain; utm did not. Mirror
// the same data into a .klikkie.com cookie to close the gap.
const ATTRIBUTION_COOKIE = '_kk_utm';

const setAttributionCookie = (utm) => {
  if (!utm || Object.keys(utm).length === 0) return;
  try {
    setFirstPartyCookie(ATTRIBUTION_COOKIE, encodeURIComponent(JSON.stringify(utm)));
  } catch (_) {
    // Ignore — cookie write failures shouldn't block landing.
  }
};

const getAttributionCookie = () => {
  try {
    const raw = getCookie(ATTRIBUTION_COOKIE);
    return raw ? JSON.parse(decodeURIComponent(raw)) || {} : {};
  } catch (_) {
    return {};
  }
};

// KL-14835: Snapchat is the one network whose URL param isn't the key we store
// it under — it sends `ScCid`
// (https://businesshelp.snapchat.com/s/article/click-id) but sGTM reads
// `user_data.scid`. Measured over 30d: `ScCid` 1,093 real ad landings, `sccid`
// 0, lowercase `scid` 315 and every one of those was our own test URL.
const STORE_AS = { ScCid: 'scid' };

/**
 * Retrieve UTM parameters from the URL and save them to the metadata of the order.
 *
 * Also synthesizes Meta's `_fbc` from `fbclid` when the Pixel hasn't set it
 * (Instagram in-app webview, cross-context returns), so server-side CAPI fired
 * from the Adyen webhook can match the conversion to the click (KL-14189).
 */
export const retrieveUtmParameters = () => {
  const utm = {};
  // Includes ad-network click IDs + ad-platform context keys so attribution
  // survives the Adyen/iDEAL redirect: if the return URL drops them,
  // retrieveUtmParameters rehydrates them from sessionStorage back onto the
  // URL (see fallthrough below). The extra keys (campaign_id, ad_id, …) come
  // from Meta/Instagram in-app webview URLs and are needed to identify the
  // ad/placement server-side for CAPI matching when the click ID is missing.
  const utmParams = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'awc',
    // coverTemplate: photobook cover slug set on ad URLs so klikkie-agent
    // (KL-13718) can preselect the exact cover the ad advertised. Piggybacks
    // the full attribution fallback chain (sessionStorage → _kk_utm cookie →
    // Adyen survival → order.attribution echo) so it survives the payment bounce.
    'coverTemplate',
    'fbclid',
    'gclid',
    'wbraid',
    'gbraid',
    'msclkid',
    'ttclid',
    'epik',
    'ScCid',
    'campaign_id',
    'ad_id',
    'adset_id',
    'placement',
    'site_source_name',
    'fbpid',
    'clid',
    'sid',
  ];

  utmParams.forEach((param) => {
    const value = getUrlParameter(param);
    if (value) {
      utm[STORE_AS[param] || param] = value;
    }
  });

  if (Object.keys(utm).length === 0) {
    // No params on this URL — rehydrate from sessionStorage (same-origin return,
    // e.g. post-Adyen redirect) or, failing that, the .klikkie.com cookie
    // (cross-subdomain hop, e.g. fr.klikkie.com landing → www.klikkie.com).
    const prevUtm = getSessionObj('utm');
    const fallback =
      prevUtm && Object.keys(prevUtm).length > 0 ? prevUtm : getAttributionCookie();
    if (!fallback || Object.keys(fallback).length === 0) return;

    // Populate the utm object from the fallback source (sessionStorage or the
    // .klikkie.com cookie). Do NOT push these keys back onto the URL — the
    // cookie + sessionStorage are the source of truth downstream (Meta CAPI,
    // order API, GA config), and URL rehydration bloats every internal
    // click after a UTM landing now that attribution-writer seeds the cookie
    // on every marketing landing (v4.20.52).
    Object.entries(fallback).forEach(([key, value]) => {
      utm[key] = value;
    });
  }

  // If a fresh fbclid is present but the Pixel hasn't (yet) written _fbc,
  // synthesize a first-party `_fbc` cookie ourselves. Server CAPI on the
  // Adyen webhook reads this cookie via the order doc later.
  const fbclid = utm.fbclid;
  if (fbclid && !getCookie('_fbc')) {
    setFirstPartyCookie('_fbc', `fb.1.${Date.now()}.${fbclid}`);
  }

  updateSessionObj('utm', utm);
  // Mirror into the .klikkie.com cookie so the next subdomain in the funnel can
  // read it back (sessionStorage can't cross origins). Uses the merged session
  // value so accumulated keys persist, not just this page's params.
  setAttributionCookie(getSessionObj('utm') || utm);
};

/**
 * Build the full attribution payload to attach to the order at create time.
 *
 * Returns the captured-at-landing ad context (utm + click IDs) plus Meta's
 * `_fbp` / `_fbc` cookies and the landing/referrer URLs. This is what the
 * Adyen webhook will read back to fire Meta CAPI / GA4 MP / Google Ads
 * ClickConversion server-side after AUTHORISATION (KL-14189).
 */
export const buildAttributionPayload = () => {
  // Cookie first (cross-subdomain survivor), session overlaid on top (same-origin, freshest).
  const utm = { ...getAttributionCookie(), ...(getSessionObj('utm') || {}) };
  const fbc = getCookie('_fbc');
  const fbp = getCookie('_fbp');
  // GA4 client_id from the _ga cookie: format "GA1.1.<client_id>.<timestamp>"
  // — the server-side Measurement Protocol fire needs this exact value.
  const gaCookie = getCookie('_ga') || '';
  const gaClientId = gaCookie.split('.').slice(-2).join('.') || null;

  // Click-ID → ad-source inference. Meta/TikTok/Bing don't auto-tag URLs with
  // utm_*, so click-only landings end up with utm_source unset and the
  // CIO conversion-channel Slack message renders empty. When a click ID is
  // present and utm_source is missing, default a sensible source/medium so
  // every conversion classifies. Google auto-tagging already populates UTMs,
  // so gclid never hits this fallback in practice.
  if (!utm.utm_source) {
    if (utm.fbclid) {
      utm.utm_source = 'facebook';
      utm.utm_medium = utm.utm_medium || 'paid_social';
    } else if (utm.ttclid) {
      utm.utm_source = 'tiktok';
      utm.utm_medium = utm.utm_medium || 'paid_social';
    } else if (utm.msclkid) {
      utm.utm_source = 'bing';
      utm.utm_medium = utm.utm_medium || 'cpc';
    } else if (utm.gclid || utm.wbraid || utm.gbraid) {
      utm.utm_source = 'google';
      utm.utm_medium = utm.utm_medium || 'cpc';
    } else if (utm.epik) {
      utm.utm_source = 'pinterest';
      utm.utm_medium = utm.utm_medium || 'paid_social';
    } else if (utm.scid) {
      utm.utm_source = 'snapchat';
      utm.utm_medium = utm.utm_medium || 'paid_social';
    } else if (utm.awc) {
      utm.utm_source = 'awin';
      utm.utm_medium = utm.utm_medium || 'affiliate';
    }
  }

  const payload = {
    ...utm,
    fbc: fbc || null,
    fbp: fbp || null,
    ga_client_id: gaClientId,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    referrer: typeof document !== 'undefined' ? document.referrer || null : null,
    landing_url: typeof window !== 'undefined' ? window.location.href : null,
  };

  // Drop null/empty so the order doc stays compact.
  return Object.fromEntries(
    Object.entries(payload).filter(([, v]) => v !== null && v !== undefined && v !== '')
  );
};
